"use client";

import { useEffect, useMemo, useRef, useState } from "react";
import { cn } from "@/lib/utils";
import { AuraPresence } from "../aura-presence";
import { Button } from "@/components/ui/button";
import type {
  DecisionTopic,
  ConversationMessage,
  TopicIntelligence,
  DecisionTopicStatus,
  MessageSourceType,
  AgentType,
  RiskLevel,
  ConfidenceLevel,
} from "@/lib/aura/types";
import {
  CheckCircle2,
  Clock,
  AlertTriangle,
  Cpu,
  FileSearch,
  History,
  Hand,
  ArrowUp,
  Paperclip,
  Circle,
  AlertOctagon,
  CheckCheck,
  Archive,
  User,
  Bot,
  Shield,
  Database,
  MessageSquare,
  X,
  Zap,
  Brain,
  FileText,
} from "lucide-react";

const DECISION_THREAD_PAGE_SIZE = 40;

// Helper Components
function StatusBadge({ status }: { status: DecisionTopicStatus }) {
  const config: Record<
    DecisionTopicStatus,
    { label: string; className: string }
  > = {
    live: { label: "Live", className: "bg-accent/20 text-accent border-accent/30" },
    "waiting-review": {
      label: "Waiting",
      className: "bg-warning/20 text-warning border-warning/30",
    },
    blocked: {
      label: "Blocked",
      className: "bg-destructive/20 text-destructive border-destructive/30",
    },
    resolved: {
      label: "Resolved",
      className: "bg-success/20 text-success border-success/30",
    },
  };

  return (
    <span
      className={cn(
        "px-2 py-0.5 text-[10px] font-medium uppercase tracking-wider rounded border",
        config[status].className
      )}
    >
      {config[status].label}
    </span>
  );
}

function StatusIcon({ status }: { status: DecisionTopicStatus }) {
  const icons: Record<DecisionTopicStatus, React.ReactNode> = {
    live: (
      <Circle className="w-2.5 h-2.5 fill-accent text-accent animate-pulse" />
    ),
    "waiting-review": <Clock className="w-3 h-3 text-warning" />,
    blocked: <AlertOctagon className="w-3 h-3 text-destructive" />,
    resolved: <CheckCheck className="w-3 h-3 text-success" />,
  };
  return icons[status];
}

type ParticipantType = AgentType | "human";

function ParticipantAvatar({
  type,
  size = "sm",
}: {
  type: ParticipantType;
  size?: "sm" | "md";
}) {
  const sizeClasses = size === "sm" ? "w-5 h-5" : "w-6 h-6";
  const iconSize = size === "sm" ? "w-3 h-3" : "w-3.5 h-3.5";

  if (type === "aura") {
    return <AuraPresence size="sm" intensity="subtle" />;
  }

  const config: Record<
    Exclude<ParticipantType, "aura">,
    { bg: string; icon: React.ReactNode }
  > = {
    codex: {
      bg: "bg-warning/20",
      icon: <Database className={cn(iconSize, "text-warning")} />,
    },
    claude: {
      bg: "bg-[#D4A574]/20",
      icon: <Brain className={cn(iconSize, "text-[#D4A574]")} />,
    },
    "founder-agent": {
      bg: "bg-success/20",
      icon: <Zap className={cn(iconSize, "text-success")} />,
    },
    core: {
      bg: "bg-muted",
      icon: <Shield className={cn(iconSize, "text-muted-foreground")} />,
    },
    human: {
      bg: "bg-foreground/10",
      icon: <User className={cn(iconSize, "text-foreground")} />,
    },
  };

  const c = config[type];

  return (
    <div
      className={cn(
        "rounded-full flex items-center justify-center",
        sizeClasses,
        c.bg
      )}
    >
      {c.icon}
    </div>
  );
}

function AuthorLabel({ source }: { source: MessageSourceType }) {
  const labels: Record<MessageSourceType, string> = {
    aura: "AURA",
    codex: "Codex",
    claude: "Claude",
    "founder-agent": "Founder Agent",
    core: "Core",
    human: "H",
    system: "System",
    evidence: "Evidence",
    approval: "Approval",
    memory: "Memory",
    "risk-warning": "Risk",
  };

  const colors: Record<MessageSourceType, string> = {
    aura: "text-accent",
    codex: "text-warning",
    claude: "text-[#D4A574]",
    "founder-agent": "text-success",
    core: "text-muted-foreground",
    human: "text-foreground",
    system: "text-muted-foreground",
    evidence: "text-accent",
    approval: "text-warning",
    memory: "text-muted-foreground",
    "risk-warning": "text-destructive",
  };

  return (
    <span className={cn("text-xs font-semibold", colors[source])}>
      {labels[source]}
    </span>
  );
}

// Topic List Item
function TopicListItem({
  topic,
  isSelected,
  onClick,
}: {
  topic: DecisionTopic;
  isSelected: boolean;
  onClick: () => void;
}) {
  const formatTime = (ts: string) => {
    const date = new Date(ts);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);

    if (diffMins < 1) return "Just now";
    if (diffMins < 60) return `${diffMins}m ago`;
    const diffHours = Math.floor(diffMins / 60);
    if (diffHours < 24) return `${diffHours}h ago`;
    return date.toLocaleDateString();
  };

  return (
    <button
      onClick={onClick}
      className={cn(
        "w-full text-left p-3 rounded-lg transition-all",
        "border border-transparent",
        isSelected ? "bg-accent/10 border-accent/30" : "hover:bg-muted/50"
      )}
    >
      <div className="flex items-start justify-between gap-2 mb-1.5">
        <h4 className="text-sm font-medium text-foreground leading-tight line-clamp-2">
          {topic.title}
        </h4>
        <StatusIcon status={topic.status} />
      </div>
      <p className="text-xs text-muted-foreground line-clamp-2 mb-2">
        {topic.summary}
      </p>
      <div className="flex items-center justify-between">
        <div className="flex items-center -space-x-1">
          {topic.participants.slice(0, 3).map((p, i) => (
            <div key={i} className="ring-1 ring-panel rounded-full">
              <ParticipantAvatar type={p.type} size="sm" />
            </div>
          ))}
          {topic.participants.length > 3 && (
            <span className="text-[10px] text-muted-foreground ml-2">
              +{topic.participants.length - 3}
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          {topic.blockerCount > 0 && (
            <div className="flex items-center gap-1">
              <AlertTriangle className="w-3 h-3 text-destructive" />
              <span className="text-[10px] text-destructive">
                {topic.blockerCount}
              </span>
            </div>
          )}
          {topic.approvalCount > 0 && (
            <div className="flex items-center gap-1">
              <Shield className="w-3 h-3 text-warning" />
              <span className="text-[10px] text-warning">
                {topic.approvalCount}
              </span>
            </div>
          )}
          <span className="text-[10px] text-muted-foreground">
            {formatTime(topic.updatedAt)}
          </span>
        </div>
      </div>
    </button>
  );
}

// Thread Message
function ThreadMessage({ message }: { message: ConversationMessage }) {
  const isHuman = message.source === "human";
  const isCore = message.source === "core";
  const isSystem = message.source === "system";
  const isEvidence = message.source === "evidence";
  const isMemory = message.source === "memory";
  const isRisk = message.source === "risk-warning";

  const formatTime = (ts: string) => {
    const date = new Date(ts);
    return date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  };

  // System/Core events
  if (isCore || isSystem) {
    return (
      <div className="flex items-center justify-center py-3">
        <div className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-muted/50 border border-border/50">
          <Database className="w-3 h-3 text-muted-foreground" />
          <span className="text-xs text-muted-foreground">
            {message.content}
          </span>
        </div>
      </div>
    );
  }

  // Evidence messages
  if (isEvidence) {
    return (
      <div className="flex gap-3 py-4">
        <div className="w-6 h-6 rounded-full bg-accent/10 flex items-center justify-center shrink-0">
          <FileText className="w-3 h-3 text-accent" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <span className="text-xs font-semibold text-accent">Evidence</span>
            <span className="text-[10px] text-muted-foreground">
              {formatTime(message.timestamp)}
            </span>
          </div>
          <p className="text-sm text-foreground/90 leading-relaxed">
            {message.content}
          </p>
        </div>
      </div>
    );
  }

  // Memory messages
  if (isMemory) {
    return (
      <div className="flex gap-3 py-4">
        <div className="w-6 h-6 rounded-full bg-muted/30 flex items-center justify-center shrink-0">
          <Database className="w-3 h-3 text-muted-foreground" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <span className="text-xs font-semibold text-muted-foreground">
              Memory
            </span>
            <span className="text-[10px] text-muted-foreground">
              {formatTime(message.timestamp)}
            </span>
          </div>
          <p className="text-sm text-muted-foreground leading-relaxed">
            {message.content}
          </p>
        </div>
      </div>
    );
  }

  // Risk warning
  if (isRisk) {
    return (
      <div className="flex gap-3 py-4">
        <div className="w-6 h-6 rounded-full bg-destructive/20 flex items-center justify-center shrink-0">
          <AlertTriangle className="w-3 h-3 text-destructive" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <span className="text-xs font-semibold text-destructive">
              Risk Warning
            </span>
            <span className="text-[10px] text-muted-foreground">
              {formatTime(message.timestamp)}
            </span>
            {message.metadata?.riskLevel && (
              <span
                className={cn(
                  "text-[10px] uppercase tracking-wider",
                  message.metadata.riskLevel === "high"
                    ? "text-destructive"
                    : message.metadata.riskLevel === "medium"
                      ? "text-warning"
                      : "text-muted-foreground"
                )}
              >
                {message.metadata.riskLevel}
              </span>
            )}
          </div>
          <p className="text-sm text-destructive/90 leading-relaxed">
            {message.content}
          </p>
        </div>
      </div>
    );
  }

  // Get participant type for avatar
  const participantType: ParticipantType = isHuman
    ? "human"
    : (message.source as AgentType);

  return (
    <div
      className={cn(
        "flex gap-3 py-4",
        isHuman && "bg-foreground/[0.02] -mx-4 px-4 rounded-lg"
      )}
    >
      <div className="shrink-0 pt-0.5">
        <ParticipantAvatar type={participantType} size="md" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-1">
          <AuthorLabel source={message.source} />
          <span className="text-[10px] text-muted-foreground">
            {formatTime(message.timestamp)}
          </span>
        </div>
        <p className="text-sm text-foreground/90 leading-relaxed">
          {message.content}
        </p>
      </div>
    </div>
  );
}

function getRiskColor(level: RiskLevel): string {
  switch (level) {
    case "critical":
    case "high":
      return "text-destructive";
    case "medium":
      return "text-warning";
    case "low":
      return "text-success";
    default:
      return "text-muted-foreground";
  }
}

function getConfidenceColor(level: ConfidenceLevel): string {
  switch (level) {
    case "verified":
    case "high":
      return "text-success";
    case "medium":
      return "text-warning";
    case "low":
      return "text-destructive";
    default:
      return "text-muted-foreground";
  }
}

function formatApprovalRole(role: string): string {
  return role
    .split("-")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
}

interface DecisionRoomProps {
  topics: DecisionTopic[];
  selectedTopicId: string | null;
  thread: ConversationMessage[];
  intelligence: TopicIntelligence | null;
  onSelectTopic?: (topicId: string) => void;
  onCloseTopic?: () => void;
  onSendMessage?: (message: string) => void;
  onAttachEvidence?: () => void;
  onAskAura?: () => void;
  onRequestClaudeReview?: () => void;
  onRecordThroughCore?: () => void;
  onAcceptRecommendation?: () => void;
  onPauseDecision?: () => void;
  onEscalateApproval?: () => void;
  onSendToCodex?: () => void;
}

export function DecisionRoom({
  topics,
  selectedTopicId,
  thread,
  intelligence,
  onSelectTopic,
  onCloseTopic,
  onSendMessage,
  onAttachEvidence,
  onAskAura,
  onRequestClaudeReview,
  onRecordThroughCore,
  onAcceptRecommendation,
  onPauseDecision,
  onEscalateApproval,
  onSendToCodex,
}: DecisionRoomProps) {
  const selectedTopic = topics.find((t) => t.id === selectedTopicId);
  const [draftContribution, setDraftContribution] = useState("");
  const [visibleMessageCount, setVisibleMessageCount] = useState(
    DECISION_THREAD_PAGE_SIZE
  );
  const threadRef = useRef<HTMLDivElement | null>(null);
  const suppressNextAutoScrollRef = useRef(false);
  const visibleThread = useMemo(
    () => thread.slice(-visibleMessageCount),
    [thread, visibleMessageCount]
  );
  const hiddenOlderMessageCount = Math.max(thread.length - visibleThread.length, 0);

  const groupedTopics = {
    live: topics.filter((t) => t.status === "live"),
    blocked: topics.filter((t) => t.status === "blocked"),
    waiting: topics.filter((t) => t.status === "waiting-review"),
    resolved: topics.filter((t) => t.status === "resolved"),
  };

  useEffect(() => {
    setVisibleMessageCount(DECISION_THREAD_PAGE_SIZE);
  }, [selectedTopicId]);

  useEffect(() => {
    if (suppressNextAutoScrollRef.current) {
      suppressNextAutoScrollRef.current = false;
      return;
    }

    const scrollToLatest = () => {
      const threadElement = threadRef.current;
      if (!threadElement) return;
      threadElement.scrollTop = threadElement.scrollHeight;
    };

    window.requestAnimationFrame(scrollToLatest);
    window.setTimeout(scrollToLatest, 100);
    window.setTimeout(scrollToLatest, 350);
    window.setTimeout(scrollToLatest, 900);
  }, [selectedTopicId, visibleThread.length, thread.length]);

  const formatTime = (ts: string) => {
    const date = new Date(ts);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);

    if (diffMins < 1) return "Just now";
    if (diffMins < 60) return `${diffMins}m ago`;
    const diffHours = Math.floor(diffMins / 60);
    if (diffHours < 24) return `${diffHours}h ago`;
    return date.toLocaleDateString();
  };

  return (
    <div className="flex-1 flex overflow-hidden">
      {/* Left Column - Decision Topic List */}
      <div className="w-[300px] shrink-0 border-r border-border bg-panel/30 flex flex-col">
        <div className="p-4 border-b border-border">
          <h2 className="text-sm font-semibold text-foreground">
            Decision Topics
          </h2>
          <p className="text-xs text-muted-foreground mt-0.5">
            {topics.length} topics
          </p>
        </div>

        <div className="flex-1 overflow-y-auto p-3 space-y-4">
          {/* Live */}
          {groupedTopics.live.length > 0 && (
            <div>
              <div className="flex items-center gap-2 px-2 mb-2">
                <Circle className="w-2 h-2 fill-accent text-accent" />
                <span className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider">
                  Live
                </span>
              </div>
              <div className="space-y-1">
                {groupedTopics.live.map((topic) => (
                  <TopicListItem
                    key={topic.id}
                    topic={topic}
                    isSelected={selectedTopicId === topic.id}
                    onClick={() => onSelectTopic?.(topic.id)}
                  />
                ))}
              </div>
            </div>
          )}

          {/* Blocked */}
          {groupedTopics.blocked.length > 0 && (
            <div>
              <div className="flex items-center gap-2 px-2 mb-2">
                <AlertOctagon className="w-3 h-3 text-destructive" />
                <span className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider">
                  Blocked
                </span>
              </div>
              <div className="space-y-1">
                {groupedTopics.blocked.map((topic) => (
                  <TopicListItem
                    key={topic.id}
                    topic={topic}
                    isSelected={selectedTopicId === topic.id}
                    onClick={() => onSelectTopic?.(topic.id)}
                  />
                ))}
              </div>
            </div>
          )}

          {/* Waiting Review */}
          {groupedTopics.waiting.length > 0 && (
            <div>
              <div className="flex items-center gap-2 px-2 mb-2">
                <Clock className="w-3 h-3 text-warning" />
                <span className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider">
                  Waiting Review
                </span>
              </div>
              <div className="space-y-1">
                {groupedTopics.waiting.map((topic) => (
                  <TopicListItem
                    key={topic.id}
                    topic={topic}
                    isSelected={selectedTopicId === topic.id}
                    onClick={() => onSelectTopic?.(topic.id)}
                  />
                ))}
              </div>
            </div>
          )}

          {/* Resolved */}
          {groupedTopics.resolved.length > 0 && (
            <div>
              <div className="flex items-center gap-2 px-2 mb-2">
                <CheckCheck className="w-3 h-3 text-success" />
                <span className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider">
                  Resolved
                </span>
              </div>
              <div className="space-y-1">
                {groupedTopics.resolved.map((topic) => (
                  <TopicListItem
                    key={topic.id}
                    topic={topic}
                    isSelected={selectedTopicId === topic.id}
                    onClick={() => onSelectTopic?.(topic.id)}
                  />
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Center Column - Live Decision Thread */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Thread Header */}
        {selectedTopic && (
          <div className="p-4 border-b border-border bg-panel/20">
            <div className="flex items-start justify-between gap-4">
              <div className="min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider">
                    Topic
                  </span>
                  <StatusBadge status={selectedTopic.status} />
                </div>
                <h1 className="text-lg font-semibold text-foreground leading-tight">
                  {selectedTopic.title}
                </h1>
              </div>
              <button
                onClick={onCloseTopic}
                className="p-1.5 rounded hover:bg-muted/50 text-muted-foreground"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="flex items-center gap-4 mt-3 text-xs text-muted-foreground">
              <div className="flex items-center gap-1.5">
                <span>Participants:</span>
                <div className="flex items-center -space-x-1">
                  {selectedTopic.participants.map((p, i) => (
                    <div key={i} className="ring-1 ring-panel rounded-full">
                      <ParticipantAvatar type={p.type} size="sm" />
                    </div>
                  ))}
                </div>
              </div>
              <span className="text-border">|</span>
              <span>Updated {formatTime(selectedTopic.updatedAt)}</span>
              {intelligence?.auraRecommendation && (
                <>
                  <span className="text-border">|</span>
                  <span className="text-accent">
                    AURA recommends: {intelligence.auraRecommendation.action}
                  </span>
                </>
              )}
            </div>
          </div>
        )}

        {/* Thread Messages */}
        <div
          ref={threadRef}
          className="flex-1 overflow-y-auto px-4"
          data-uat="decision-room-thread"
        >
          <div className="max-w-3xl mx-auto divide-y divide-border/30">
            {hiddenOlderMessageCount > 0 && (
              <div className="flex justify-center py-3">
                <button
                  type="button"
                  className="rounded-full border border-border/60 bg-panel/70 px-3 py-1 text-[11px] text-muted-foreground transition-colors hover:border-accent/40 hover:text-foreground"
                  onClick={() => {
                    suppressNextAutoScrollRef.current = true;
                    setVisibleMessageCount((current) =>
                      Math.min(current + DECISION_THREAD_PAGE_SIZE, thread.length)
                    );
                  }}
                >
                  Load {Math.min(DECISION_THREAD_PAGE_SIZE, hiddenOlderMessageCount)} older
                  messages · {hiddenOlderMessageCount} hidden
                </button>
              </div>
            )}
            {visibleThread.map((message) => (
              <ThreadMessage key={message.id} message={message} />
            ))}
          </div>
        </div>

        {/* Human Input Composer */}
        <div className="p-4 border-t border-border bg-panel/20">
          <div className="max-w-3xl mx-auto">
            <div className="glass-panel rounded-lg p-3 border border-border/50">
              <textarea
                value={draftContribution}
                onChange={(event) => setDraftContribution(event.target.value)}
                onKeyDown={(event) => {
                  if ((event.metaKey || event.ctrlKey) && event.key === "Enter") {
                    event.preventDefault();
                    if (draftContribution.trim()) {
                      onSendMessage?.(draftContribution.trim());
                      setDraftContribution("");
                    }
                  }
                }}
                placeholder="Add your opinion to this decision..."
                className="w-full bg-transparent text-sm text-foreground placeholder:text-muted-foreground resize-none focus:outline-none min-h-[60px]"
                rows={2}
              />
              <div className="flex items-center justify-between mt-2 pt-2 border-t border-border/30">
                <div className="flex items-center gap-1">
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-7 px-2 text-xs text-muted-foreground hover:text-foreground"
                    onClick={onAttachEvidence}
                  >
                    <Paperclip className="w-3.5 h-3.5 mr-1" />
                    Attach Evidence
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-7 px-2 text-xs text-muted-foreground hover:text-foreground"
                    onClick={onAskAura}
                  >
                    <MessageSquare className="w-3.5 h-3.5 mr-1" />
                    Ask AURA
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-7 px-2 text-xs text-muted-foreground hover:text-foreground"
                    onClick={onRequestClaudeReview}
                  >
                    <FileSearch className="w-3.5 h-3.5 mr-1" />
                    Request Claude Review
                  </Button>
                </div>
                <Button
                  size="sm"
                  className="h-7 px-3 text-xs bg-accent/20 text-accent hover:bg-accent/30 border border-accent/30"
                  onClick={() => {
                    if (draftContribution.trim()) {
                      onSendMessage?.(draftContribution.trim());
                      setDraftContribution("");
                      return;
                    }
                    onRecordThroughCore?.();
                  }}
                >
                  <Database className="w-3.5 h-3.5 mr-1.5" />
                  Record through Core
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Right Column - Topic Intelligence Panel */}
      <div className="w-[380px] shrink-0 border-l border-border bg-panel/30 overflow-y-auto">
        <div className="p-4 space-y-5">
          {/* Evidence Summary */}
          {intelligence && (
            <>
              <section>
                <h3 className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider mb-3">
                  Evidence Summary
                </h3>
                <div className="glass-panel rounded-lg p-3">
                  <p className="text-xs text-foreground mb-3">
                    {intelligence.evidenceSummary}
                  </p>
                  <div className="space-y-2">
                    {intelligence.evidenceItems.map((item) => (
                      <div
                        key={item.id}
                        className="flex items-center justify-between py-1 border-t border-border/30 first:border-0 first:pt-0"
                      >
                        <span className="text-xs text-muted-foreground">
                          {item.title}
                        </span>
                        <span
                          className={cn(
                            "text-xs font-medium",
                            getConfidenceColor(item.confidence)
                          )}
                        >
                          {item.confidence}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              </section>

              {/* Risk / Impact */}
              <section>
                <h3 className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider mb-3">
                  Risk / Impact
                </h3>
                <div className="glass-panel rounded-lg p-3 space-y-3">
                  <div className="flex items-start gap-2">
                    <AlertOctagon
                      className={cn(
                        "w-3.5 h-3.5 mt-0.5 shrink-0",
                        getRiskColor(intelligence.risk.level)
                      )}
                    />
                    <div>
                      <p className="text-xs text-foreground font-medium">
                        Risk Level
                      </p>
                      <p
                        className={cn(
                          "text-[11px]",
                          getRiskColor(intelligence.risk.level)
                        )}
                      >
                        {intelligence.risk.level.charAt(0).toUpperCase() +
                          intelligence.risk.level.slice(1)}
                      </p>
                    </div>
                  </div>
                  {intelligence.risk.factors.map((factor, i) => (
                    <div key={i} className="flex items-start gap-2">
                      <AlertTriangle className="w-3.5 h-3.5 text-warning mt-0.5 shrink-0" />
                      <p className="text-[11px] text-muted-foreground">
                        {factor}
                      </p>
                    </div>
                  ))}
                  <div className="flex items-start gap-2">
                    <Shield className="w-3.5 h-3.5 text-muted-foreground mt-0.5 shrink-0" />
                    <div>
                      <p className="text-xs text-foreground font-medium">
                        Scope
                      </p>
                      <p className="text-[11px] text-muted-foreground">
                        {intelligence.impact.scope} -{" "}
                        {intelligence.impact.affectedSystems.join(", ")}
                      </p>
                    </div>
                  </div>
                </div>
              </section>

              {/* Approval Requirement */}
              <section>
                <h3 className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider mb-3">
                  Approval Requirement
                </h3>
                <div className="glass-panel rounded-lg p-3 border-l-2 border-l-warning">
                  <div className="flex items-center gap-2 mb-2">
                    <Shield className="w-4 h-4 text-warning" />
                    <span className="text-sm text-foreground font-medium">
                      {formatApprovalRole(
                        intelligence.approvalRequirement.role
                      )}
                    </span>
                  </div>
                  <p className="text-[11px] text-muted-foreground">
                    {intelligence.approvalRequirement.reason}
                  </p>
                </div>
              </section>

              {/* Recommended Decision */}
              <section>
                <h3 className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider mb-3">
                  Recommended Decision
                </h3>
                <div className="glass-panel rounded-lg p-3 border border-accent/20 bg-accent/5">
                  <div className="flex items-center gap-2 mb-2">
                    <AuraPresence size="sm" intensity="subtle" />
                    <span className="text-xs font-semibold text-accent">
                      AURA
                    </span>
                    <span
                      className={cn(
                        "text-[10px]",
                        getConfidenceColor(
                          intelligence.auraRecommendation.confidence
                        )
                      )}
                    >
                      ({intelligence.auraRecommendation.confidence} confidence)
                    </span>
                  </div>
                  <p className="text-sm text-foreground leading-relaxed mb-2">
                    {intelligence.auraRecommendation.action}
                  </p>
                  <p className="text-[11px] text-muted-foreground">
                    {intelligence.auraRecommendation.reasoning}
                  </p>
                </div>
              </section>

              {/* Decision History */}
              <section>
                <h3 className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider mb-3 flex items-center gap-2">
                  <History className="w-3 h-3" />
                  Decision History
                </h3>
                <div className="space-y-2">
                  {intelligence.history.map((entry) => (
                    <div key={entry.id} className="flex items-center gap-2 text-xs">
                      <div className="w-1 h-1 rounded-full bg-muted-foreground" />
                      <span className="text-muted-foreground">
                        {entry.action}
                      </span>
                      <span className="text-muted-foreground/60">
                        ({entry.actor})
                      </span>
                    </div>
                  ))}
                </div>
              </section>
            </>
          )}

          {/* Actions */}
          <section className="pt-2 border-t border-border">
            <h3 className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider mb-3">
              Actions
            </h3>
            <div className="space-y-2">
              <Button
                className="w-full justify-start h-9 text-xs bg-accent/20 text-accent hover:bg-accent/30 border border-accent/30"
                onClick={onAcceptRecommendation}
              >
                <CheckCircle2 className="w-3.5 h-3.5 mr-2" />
                Accept AURA Recommendation
              </Button>
              <Button
                variant="outline"
                className="w-full justify-start h-9 text-xs"
                onClick={onRequestClaudeReview}
              >
                <FileSearch className="w-3.5 h-3.5 mr-2" />
                Ask Claude to Review
              </Button>
              <Button
                variant="outline"
                className="w-full justify-start h-9 text-xs"
                onClick={onSendToCodex}
              >
                <Cpu className="w-3.5 h-3.5 mr-2" />
                Send Codex to Implement
              </Button>
              <Button
                variant="outline"
                className="w-full justify-start h-9 text-xs border-warning/30 text-warning hover:bg-warning/10 hover:text-warning"
                onClick={onPauseDecision}
              >
                <Hand className="w-3.5 h-3.5 mr-2" />
                Pause Decision
              </Button>
              <Button
                variant="outline"
                className="w-full justify-start h-9 text-xs"
                onClick={onEscalateApproval}
              >
                <ArrowUp className="w-3.5 h-3.5 mr-2" />
                Escalate Approval
              </Button>
              <Button
                variant="ghost"
                className="w-full justify-start h-9 text-xs text-muted-foreground"
                onClick={onCloseTopic}
              >
                <X className="w-3.5 h-3.5 mr-2" />
                Close Topic
              </Button>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
}
